import type { GenericError } from '@skynet/data-ws-core';
import type { JsonExchange } from '@skynet/data-shared-exchanges';
import type { JsonAccountError } from '@skynet/data-shared-accounts';

/** MM admin channel commands the servers slice handles. */
export enum ServersWsCommands {
  Servers = 'Servers',
  AccountDetails = 'AccountDetails',

  EnableAccount = 'EnableAccount',
  DisableAccount = 'DisableAccount',
}

export interface ServersState {
  error: null | GenericError;

  servers: JsonServer[];
  isServersLoading: boolean;
}

export interface JsonTimer {
  name: string; // 'Monitor.Run1Minute'
  averageLoop: number; // 3.44876791256281
  overtimePercentage: number; // 0.25
}

/** Server identity as the backend sends it (PascalCase). */
export interface JsonInitialData {
  Version: string;
  Name: string;
  IPAddress: string;
  LastSocketTime: string; // "2023-04-16T06:36:40.5109389Z",
  LastWebSocketTime: string; // "2023-04-16T06:41:44.324337Z",
  StartSocketTime: string; // "0001-01-01T00:00:00",
  StartWebSocketTime: string; // "2023-04-16T06:36:38.5744129Z",
  HimikoDatabase: string;
  HimikoDatabaseConnected: boolean;
  Proxy: string;
  LoadingSeconds: number;
  MonitorPing: number;
  HimikoPing: number;
  NoResponseCount: number;
  ErrorsWithChart: boolean;
  TooMuchRequestsException: string | null;
  NoSymbolException: string | null;
  WrongAPIKeyException: string | null;
  WrongIPException: string | null;
}

export interface InitialDataCamelCase {
  himikoVersion: string;
  name: string;
  ipAddress: string;
  lastSocketTime: string;
  lastWebSocketTime: string;
  startSocketTime: string;
  startWebSocketTime: string;
  himikoDatabase: string;
  himikoDatabaseConnected: boolean;
  proxy: string;
  loadingSeconds: number;
  monitorPing: number;
  himikoPing: number;
  noResponseCount: number;
  errorsWithChart: boolean;
  tooMuchRequestsException: string | null;
  noSymbolException: string | null;
  wrongApiKeyException: string | null;
  wrongIpException: string | null;
}

export interface MonitorStatus {
  id: number;
  version: string;
  buildDate: string; // "built 14 April 15:13"
  averageRequestsPerMinute: number;
  tooMuchRequestPerMinute: number;
  monitorErrorsInLog: number;
  himikoErrorsInLog: number;
  errorRequestsPercentage: number;
  errorBalancePercentage: number;
  getSummaryAvailable: boolean;
  loadChartAvailable: boolean;
  getFeeRateAvailable: boolean;
  getTransactionsAvailable: boolean;
  sentTime: string; // "2023-04-16 06:41:29",
  lastUpdateTime: string; // "2023-04-16 06:41:25",
  priceUpdated: boolean;
  balancesUpdated: boolean;
  accounts: JsonAccountError[];
  timers: JsonTimer[];
  chartsUpdated: number[]; // [1000, 60]
  bases: string[]; // ['USD', 'BTC' ...]
  blockedTickers: string[]; // ["CCD/BTC/BFNX", "CHEX/USD/BFNX", ...]
  status: string;
}

export interface JsonServersSuccess {
  command: 'Servers';
  statuses: {
    initialData: JsonInitialData;
    monitorStatus: MonitorStatus;
    description: string;
    lastUpdateTime: string; // "2023-04-16 06:41:25",
  }[];
}

export type JsonServer = {
  description: string;
  lastUpdateTime: string;
} & MonitorStatus &
  InitialDataCamelCase;

/** A server row joined with its exchange from the shared exchanges slice. */
export type JsonServerExchange = JsonServer & { exchange?: JsonExchange };
